import React from 'react';
import { Chip, Tooltip } from '@mui/material';

interface ReadinessBadgeProps {
  ready: boolean;
  missingFields?: string[];
}

export function prettifyMissingField(field: string): string | null {
  const raw = String(field || '').trim();
  if (!raw) return null;

  // PLU is not required client-side; backend can auto-manage it.
  if (/\bpluNo\b/i.test(raw)) return null;

  const lineMatch = raw.match(/^line\[(\d+)\]\.(.+)$/i);
  if (lineMatch) {
    const idx = Number(lineMatch[1]);
    const inner = prettifyMissingField(lineMatch[2]);
    const lineLabel = Number.isFinite(idx) ? `Rad ${idx + 1}` : 'Rad';
    return inner ? `${lineLabel}: ${inner}` : null;
  }

  const skuMatch = raw.match(/^sku\[(\d+)\]\.(.+)$/i);
  if (skuMatch) {
    const idx = Number(skuMatch[1]);
    const key = skuMatch[2];
    const skuLabel = Number.isFinite(idx) ? `SKU ${idx + 1}` : 'SKU';
    const map: Record<string, string> = {
      attribute1Code: 'Färg',
      attribute1CodeNotInSystem: 'Färg saknas i systemet',
      attribute2Code: 'Storlek',
      attribute2CodeNotInSystem: 'Storlek saknas i systemet',
      attribute3CodeNotInSystem: 'Längd saknas i systemet',
      ean13: 'EAN13',
      articleName: 'Artikelnamn',
      supplierPrice: 'Inköpspris',
      priceOrig: 'Ord. pris',
      price: 'Pris',
      weight: 'Vikt',
    };
    const prettyKey = map[key] || key;
    return `${skuLabel}: ${prettyKey}`;
  }

  const map: Record<string, string> = {
    supplierId: 'Leverantör',
    orderNumber: 'Ordernummer',
    supplierOrderNumber: 'Leverantörens ordernr',
    season: 'Säsong',
    defaultDeliveryMonth: 'Standard leveransmånad',
    paymentTermsDays: 'Betalningsvillkor',
    notes: 'Anteckningar',
    attribute1Code: 'Färg',
    attribute1CodeNotInSystem: 'Färg saknas i systemet',
    attribute2Code: 'Storlek',
    attribute2CodeNotInSystem: 'Storlek saknas i systemet',
    attribute3CodeNotInSystem: 'Längd saknas i systemet',
  };

  return map[raw] || raw;
}

export function formatMissingFields(missingFields?: string[]): string {
  const prettyMissing = Array.from(
    new Set(
      (missingFields || [])
        .map(prettifyMissingField)
        .filter((v): v is string => Boolean(v)),
    ),
  );
  return prettyMissing.length > 0 ? prettyMissing.join(', ') : 'Saknar data';
}

/** Size code applied when force-transferring orders with missing sizes. */
export const FORCE_TRANSFER_MISSING_SIZE_CODE = 'ONESIZE';

/** True when the only blocking readiness issues are missing attribute2Code (storlek). */
export function canForceTransferWithMissingSize(
  missingFields?: string[],
): boolean {
  if (!Array.isArray(missingFields) || missingFields.length === 0) return false;

  const relevant = missingFields.filter((field) => {
    const raw = String(field || '').trim();
    if (!raw) return false;
    if (/\bpluNo\b/i.test(raw)) return false;
    return true;
  });

  if (relevant.length === 0) return false;

  return relevant.every((field) =>
    /(?:^|\.)attribute2Code$/i.test(String(field).trim()),
  );
}

const ReadinessBadge: React.FC<ReadinessBadgeProps> = ({
  ready,
  missingFields,
}) => {
  if (ready) {
    return <Chip size="small" color="success" label="Redo att överföras" />;
  }

  // If the caller doesn't provide missingFields (e.g. list views),
  // we cannot conclude anything about completeness.
  if (!Array.isArray(missingFields)) {
    return (
      <Tooltip title="Öppna ordern för att se om något saknas">
        <Chip size="small" color="default" label="Okänt" />
      </Tooltip>
    );
  }

  const rawMissing = Array.from(
    new Set(
      (missingFields || []).filter((f) => {
        const raw = String(f || '').trim();
        if (!raw) return false;
        // PLU is handled server-side (auto-generated) when needed.
        if (/\bpluNo\b/i.test(raw)) return false;
        return true;
      }),
    ),
  );

  const prettyLabel = formatMissingFields(missingFields);
  const label =
    prettyLabel !== 'Saknar data'
      ? prettyLabel
      : rawMissing.length > 0
        ? rawMissing.join(', ')
        : 'Saknar data';

  if (rawMissing.length === 0) {
    return (
      <Tooltip title="Ingen data saknas">
        <Chip size="small" color="success" label="Data komplett" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={label}>
      <Chip size="small" color="warning" label="Saknar data" />
    </Tooltip>
  );
};

export default ReadinessBadge;
